import React, { useState, useEffect } from "react";
import {
  Modal,
  View,
  Text,
  Pressable,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../theme";

// ─── Font helper ──────────────────────────────────────────────────────────────
const inter = (w: "400" | "500" | "600" | "700") =>
  Platform.select({ web: { fontFamily: "Inter, sans-serif", fontWeight: w }, default: {} });

// ─── Types ────────────────────────────────────────────────────────────────────
export interface AllOffDutyRange {
  startDate: string; // "YYYY-MM-DD"
  endDate: string;   // "YYYY-MM-DD"
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
}

interface Props {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (range: AllOffDutyRange) => void | Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const todayStr = (): string => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const nowTimeStr = (): string => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const formatDateDisplay = (dateStr: string): string => {
  if (!dateStr) return "";
  const [y, m, day] = dateStr.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(day, 10)}, ${y}`;
};

const formatTimeDisplay = (timeStr: string): string => {
  if (!timeStr) return "";
  const [hStr, mStr] = timeStr.split(":");
  const h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${mStr} ${ampm}`;
};

// ─── Date input (web uses <input type="date">, native uses text) ───────────────
const DateInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  min?: string;
  isDark?: boolean;
}> = ({ label, value, onChange, error, min, isDark }) => {
  if (Platform.OS === "web") {
    return (
      <View style={inputStyles.fieldWrap}>
        <Text style={[inputStyles.fieldLabel, isDark && { color: "#B0B0B0" }]}>{label}</Text>
        {React.createElement("input", {
          type: "date",
          value,
          min,
          onChange: (e: any) => onChange(e.target.value),
          style: {
            height: 38,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: error ? "#c0392b" : (isDark ? "#404040" : "#D1D5DB"),
            borderRadius: 8,
            paddingLeft: 10,
            paddingRight: 8,
            fontSize: 13,
            color: isDark ? "#F2F2F2" : "#1a1a1a",
            backgroundColor: isDark ? "#2B2B2B" : "#fff",
            fontFamily: "Inter, sans-serif",
            outline: "none",
            width: "100%",
            boxSizing: "border-box",
            colorScheme: isDark ? "dark" : "light",
          },
        })}
        {error ? <Text style={inputStyles.fieldError}>{error}</Text> : null}
      </View>
    );
  }
  return (
    <View style={inputStyles.fieldWrap}>
      <Text style={[inputStyles.fieldLabel, isDark && { color: "#B0B0B0" }]}>{label}</Text>
      <View style={[inputStyles.nativeDateBox, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, error ? inputStyles.nativeDateBoxErr : null]}>
        <Text style={[inputStyles.nativeDateText, isDark && { color: "#F2F2F2" }]}>{value ? formatDateDisplay(value) : "Select date"}</Text>
        <MaterialIcons name="calendar-today" size={15} color={isDark ? "#6E6E6E" : "#6b7280"} />
      </View>
      {error ? <Text style={inputStyles.fieldError}>{error}</Text> : null}
    </View>
  );
};

// ─── Time input (web uses <input type="time">) ─────────────────────────────────
const TimeInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  isDark?: boolean;
}> = ({ label, value, onChange, error, isDark }) => {
  if (Platform.OS === "web") {
    return (
      <View style={inputStyles.fieldWrap}>
        <Text style={[inputStyles.fieldLabel, isDark && { color: "#B0B0B0" }]}>{label}</Text>
        {React.createElement("input", {
          type: "time",
          value,
          onChange: (e: any) => onChange(e.target.value),
          style: {
            height: 38,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: error ? "#c0392b" : (isDark ? "#404040" : "#D1D5DB"),
            borderRadius: 8,
            paddingLeft: 10,
            paddingRight: 8,
            fontSize: 13,
            color: isDark ? "#F2F2F2" : "#1a1a1a",
            backgroundColor: isDark ? "#2B2B2B" : "#fff",
            fontFamily: "Inter, sans-serif",
            outline: "none",
            width: "100%",
            boxSizing: "border-box",
            colorScheme: isDark ? "dark" : "light",
          },
        })}
        {error ? <Text style={inputStyles.fieldError}>{error}</Text> : null}
      </View>
    );
  }
  return (
    <View style={inputStyles.fieldWrap}>
      <Text style={[inputStyles.fieldLabel, isDark && { color: "#B0B0B0" }]}>{label}</Text>
      <View style={[inputStyles.nativeDateBox, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, error ? inputStyles.nativeDateBoxErr : null]}>
        <Text style={[inputStyles.nativeDateText, isDark && { color: "#F2F2F2" }]}>{value ? formatTimeDisplay(value) : "Select time"}</Text>
        <MaterialIcons name="access-time" size={15} color={isDark ? "#6E6E6E" : "#6b7280"} />
      </View>
      {error ? <Text style={inputStyles.fieldError}>{error}</Text> : null}
    </View>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const AllOffDutyModal: React.FC<Props> = ({ visible, onCancel, onSubmit }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate]     = useState(todayStr);
  const [startTime, setStartTime] = useState(nowTimeStr);
  const [endTime, setEndTime]     = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Reset fields each time the modal opens
  useEffect(() => {
    if (visible) {
      const today = todayStr();
      const now   = nowTimeStr();
      const d = new Date();
      d.setHours(d.getHours() + 1);
      const nextHour = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      setStartDate(today);
      setEndDate(today);
      setStartTime(now);
      setEndTime(nextHour);
      setErrors({});
      setSubmitting(false);
    }
  }, [visible]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!startDate) errs.startDate = "Start date is required.";
    if (!endDate)   errs.endDate   = "End date is required.";
    if (!startTime) errs.startTime = "Start time is required.";
    if (!endTime)   errs.endTime   = "End time is required.";

    if (startDate && endDate && startTime && endTime) {
      const start = new Date(`${startDate}T${startTime}`);
      const end   = new Date(`${endDate}T${endTime}`);
      if (end <= start) {
        errs.endDate = "End date/time must be after start date/time.";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({ startDate, endDate, startTime, endTime });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setErrors({});
    onCancel();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleCancel}>
      <View style={ms.backdrop}>
        <View style={[ms.card, isDark && { backgroundColor: "#202020" }]}>
          {/* Header */}
          <View style={[ms.header, isDark && { borderBottomColor: "#363636" }]}>
            <View style={[ms.headerIconWrap, isDark && { backgroundColor: "#0d2a44" }]}>
              <MaterialIcons name="people-outline" size={20} color={isDark ? "#3D8BFF" : "#034895"} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[ms.title, isDark && { color: "#F2F2F2" }]}>Set Off Duty Period</Text>
              <Text style={[ms.subtitle, isDark && { color: "#B0B0B0" }]}>
                All team members will be marked off duty for this window.
                "System Issue" tickets will automatically create an INC in SNOW.
              </Text>
            </View>
          </View>

          {/* Body */}
          <View style={ms.body}>
            {/* Date row */}
            <View style={ms.row}>
              <View style={{ flex: 1 }}>
                <DateInput
                  label="Start Date *"
                  value={startDate}
                  onChange={(v) => { setStartDate(v); setErrors((e) => { const n = {...e}; delete n.startDate; return n; }); }}
                  error={errors.startDate}
                  min={todayStr()}
                  isDark={isDark}
                />
              </View>
              <View style={ms.rowSep} />
              <View style={{ flex: 1 }}>
                <DateInput
                  label="End Date *"
                  value={endDate}
                  onChange={(v) => { setEndDate(v); setErrors((e) => { const n = {...e}; delete n.endDate; return n; }); }}
                  error={errors.endDate}
                  min={startDate || todayStr()}
                  isDark={isDark}
                />
              </View>
            </View>

            {/* Time row */}
            <View style={[ms.row, { marginTop: 14 }]}>
              <View style={{ flex: 1 }}>
                <TimeInput
                  label="Start Time *"
                  value={startTime}
                  onChange={(v) => { setStartTime(v); setErrors((e) => { const n = {...e}; delete n.startTime; return n; }); }}
                  error={errors.startTime}
                  isDark={isDark}
                />
              </View>
              <View style={ms.rowSep} />
              <View style={{ flex: 1 }}>
                <TimeInput
                  label="End Time *"
                  value={endTime}
                  onChange={(v) => { setEndTime(v); setErrors((e) => { const n = {...e}; delete n.endTime; return n; }); }}
                  error={errors.endTime}
                  isDark={isDark}
                />
              </View>
            </View>

            {/* Info note */}
            <View style={[ms.infoNote, isDark && { backgroundColor: "#1a1a1a", borderColor: "#363636" }]}>
              <MaterialIcons name="info-outline" size={14} color={isDark ? "#6E6E6E" : "#818181"} />
              <Text style={[ms.infoNoteText, isDark && { color: "#B0B0B0" }]}>
                During this period, all "System Issue" category tickets will automatically create an incident (INC) in ServiceNow.
              </Text>
            </View>
          </View>

          {/* Footer */}
          <View style={[ms.footer, isDark && { backgroundColor: "#1a1a1a", borderTopColor: "#363636" }]}>
            <Pressable
              style={({ pressed }) => [ms.btnGhost, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, pressed && ms.btnGhostPressed]}
              onPress={handleCancel}
              disabled={submitting}
            >
              <Text style={[ms.btnGhostText, isDark && { color: "#F2F2F2" }]}>Cancel</Text>
            </Pressable>
            <Pressable
              style={({ pressed }) => [ms.btnPrimary, (pressed || submitting) && ms.btnPrimaryPressed]}
              onPress={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={ms.btnPrimaryText}>Confirm</Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// ─── Input sub-styles ─────────────────────────────────────────────────────────
const inputStyles = StyleSheet.create({
  fieldWrap:      { flex: 1 },
  fieldLabel:     { fontSize: 12, color: "#4b5563", marginBottom: 6, ...(inter("600") as any) },
  fieldError:     { fontSize: 11, color: "#c0392b", marginTop: 4, ...(inter("400") as any) },
  nativeDateBox:  { height: 38, flexDirection: "row", alignItems: "center", justifyContent: "space-between", borderWidth: 1, borderColor: "#D1D5DB", borderRadius: 8, paddingHorizontal: 12, backgroundColor: "#fff" },
  nativeDateBoxErr: { borderColor: "#c0392b" },
  nativeDateText: { fontSize: 13, color: "#1a1a1a", ...(inter("400") as any) },
});

// ─── Modal styles ─────────────────────────────────────────────────────────────
const ms = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  card: {
    width: "100%" as any,
    maxWidth: 520,
    backgroundColor: "#fff",
    borderRadius: 14,
    ...({
      boxShadow: "0 12px 48px rgba(0,20,60,0.22)",
    } as any),
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 14,
    paddingHorizontal: 24,
    paddingTop: 22,
    paddingBottom: 18,
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
  },
  headerIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: "#EEF5FF",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  title: {
    fontSize: 16,
    color: "#1a1a1a",
    marginBottom: 4,
    ...(inter("700") as any),
  },
  subtitle: {
    fontSize: 13,
    color: "#6b7280",
    lineHeight: 19,
    ...(inter("400") as any),
  },
  body: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  rowSep: {
    width: 14,
  },
  infoNote: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 18,
    backgroundColor: "#F7F9FC",
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: "#E5E9F0",
  },
  infoNoteText: {
    flex: 1,
    fontSize: 12,
    color: "#6b7280",
    lineHeight: 17,
    ...(inter("400") as any),
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
    backgroundColor: "#FAFBFD",
  },
  btnGhost: {
    paddingHorizontal: 20,
    paddingVertical: 9,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "#fff",
  },
  btnGhostPressed: { backgroundColor: "#F3F4F6" },
  btnGhostText: {
    fontSize: 14,
    color: "#4b4b4b",
    ...(inter("500") as any),
  },
  btnPrimary: {
    paddingHorizontal: 22,
    paddingVertical: 9,
    borderRadius: 20,
    backgroundColor: "#034895",
    minWidth: 100,
    alignItems: "center",
    justifyContent: "center",
  },
  btnPrimaryPressed: { backgroundColor: "#023070" },
  btnPrimaryText: {
    fontSize: 14,
    color: "#fff",
    ...(inter("700") as any),
  },
});

export default AllOffDutyModal;
